/**
 * Comments on nodes in the cloud (utils/nodeComments.ts):
 *
 *   projects/{projectId}/comments/{commentId}    one comment, about one node
 *
 * Everyone a workspace is shared with reads and writes them, as themselves, under the rules
 * (firestore.rules, "Comments on nodes"); also those who may only view it, since giving feedback
 * is what a reviewer does. Each comment is its own record, so people writing at the same time
 * never write over each other.
 */
import { getFirestore, collection, doc, onSnapshot, setDoc, deleteDoc } from 'firebase/firestore';
import { getFirebaseApp, isFirebaseActive, withCloudTimeout, describeCloudError } from './storage';
import { cleanComment, toComment, type NodeComment } from './nodeComments';

/** The signed-in person, as they sign a comment */
export interface CommentAuthor { id: string; name: string; picture?: string }

export const commentsAvailable = (): boolean => isFirebaseActive();

const db = () => {
    const app = getFirebaseApp();
    if (!app) throw new Error('Comments need the cloud. Sign in to use them.');
    return getFirestore(app);
};

const comments = (projectId: string) => collection(db(), 'projects', projectId, 'comments');

/** A refusal from the rules most likely means the comments section of firestore.rules is not deployed yet */
const describe = (e: unknown, fallback: string): string =>
    ((e as { code?: string })?.code === 'permission-denied'
        ? `${fallback}: the database does not allow comments yet. The comments rules in firestore.rules need to be deployed.`
        : describeCloudError(e, fallback));

type Stop = () => void;

/** Every comment of a workspace, as they change */
export const watchComments = (projectId: string, onComments: (comments: NodeComment[]) => void, onError: (message: string) => void): Stop =>
    onSnapshot(
        comments(projectId),
        (snapshot) => onComments(snapshot.docs.map(d => toComment(d.id, d.data() as Record<string, unknown>)).filter((c): c is NodeComment => c !== null)),
        (error) => { console.error('[comments] listener failed:', error); onError(describe(error, 'The comments could not be loaded')); },
    );

/** A new comment on a node, written at once; null when the text is empty */
export const addComment = async (projectId: string, nodeId: string, text: string, author: CommentAuthor): Promise<NodeComment | null> => {
    const clean = cleanComment(text);
    if (!clean) return null;
    const ref = doc(comments(projectId));
    const comment: NodeComment = {
        id: ref.id, nodeId, text: clean, authorId: author.id, authorName: author.name || 'Someone',
        ...(author.picture ? { authorPicture: author.picture } : {}), createdAt: Date.now(),
    };
    const { id: _id, ...data } = comment;
    try {
        await withCloudTimeout(() => setDoc(ref, data));
    } catch (e) {
        console.error('[comments] add failed:', e);
        throw new Error(describe(e, 'The comment could not be sent'));
    }
    return comment;
};

/** Delete a comment for good */
export const deleteComment = async (projectId: string, commentId: string): Promise<void> => {
    try {
        await withCloudTimeout(() => deleteDoc(doc(comments(projectId), commentId)));
    } catch (e) {
        console.error('[comments] delete failed:', e);
        throw new Error(describe(e, 'The comment could not be deleted'));
    }
};
